/*
 * <gs-header-search> — search overlay panel for <gs-header> (Task 3).
 *
 * - Focusing the header pill input opens the `search` panel and moves focus to
 *   the big panel input (the pill value is carried over and mirrored back on close).
 * - Empty query  -> "Popular Products" (server-rendered tabs).
 * - Typed query  -> debounced Section Rendering API request to
 *   sections/gs-header-search-results.liquid (queries + products), stale requests aborted.
 * - Combobox keyboard model: ArrowUp/Down move a virtual selection
 *   (aria-activedescendant), Enter opens it, or submits /search when nothing is selected.
 * - Esc / outside click / close button are handled by <gs-header>; we only restore focus.
 */
if (!customElements.get('gs-header-search')) {
  const DEBOUNCE_MS = 220;
  /* Matches the breakpoint where assets/gs-header-search.css switches the panel
     to a full-screen fixed overlay. */
  const MOBILE_QUERY = '(max-width: 1360px)';

  class GSHeaderSearch extends HTMLElement {
    constructor() {
      super();
      this._cache = new Map();
      this._active = -1;
      this._abort = null;
      this._timer = null;
      this._restoring = false;
      this._closedByUser = false;
      this._onPillFocus = this._onPillFocus.bind(this);
      this._onOpen = this._onOpen.bind(this);
      this._onClose = this._onClose.bind(this);
      this._onInput = this._onInput.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
      this._onResultsClick = this._onResultsClick.bind(this);
      this._onBlockSelect = this._onBlockSelect.bind(this);
      this._onBlockDeselect = this._onBlockDeselect.bind(this);
    }

    connectedCallback() {
      this.header = this.closest('gs-header');
      this.input = this.querySelector('[data-gs-search-input]');
      this.form = this.querySelector('[data-gs-search-form]');
      this.body = this.querySelector('[data-gs-search-body]');
      this.idle = this.querySelector('[data-gs-search-idle]');
      this.live = this.querySelector('[data-gs-search-live]');
      this.loading = this.querySelector('[data-gs-search-loading]');
      this.results = this.querySelector('[data-gs-search-results]');
      this.status = this.querySelector('[data-gs-search-status]');
      if (!this.header || !this.input) return;

      this.pill = this.header.querySelector('[data-gs-header-search-input]');
      this.pillForm = this.header.querySelector('[data-gs-header-search-form]');

      if (this.pillForm) {
        this.pillForm.addEventListener('focusin', this._onPillFocus);
        this.pillForm.addEventListener('click', this._onPillFocus);
      }
      this.header.addEventListener('gs-header:open', this._onOpen);
      this.header.addEventListener('gs-header:close', this._onClose);
      this.input.addEventListener('input', this._onInput);
      this.addEventListener('keydown', this._onKeydown);
      this.form.addEventListener('submit', this._onSubmit);
      this.results.addEventListener('click', this._onResultsClick);
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-gs-header-close]')) this._closedByUser = true;
      });
      document.addEventListener('shopify:block:select', this._onBlockSelect);
      document.addEventListener('shopify:block:deselect', this._onBlockDeselect);
    }

    disconnectedCallback() {
      if (this.pillForm) {
        this.pillForm.removeEventListener('focusin', this._onPillFocus);
        this.pillForm.removeEventListener('click', this._onPillFocus);
      }
      if (this.header) {
        this.header.removeEventListener('gs-header:open', this._onOpen);
        this.header.removeEventListener('gs-header:close', this._onClose);
      }
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this._onBlockDeselect);
      clearTimeout(this._timer);
      if (this._abort) this._abort.abort();
      this._lockScroll(false);
    }

    /* ---------- open / close ---------- */

    _onPillFocus(event) {
      if (this._restoring || !this.header) return;
      if (event && event.type === 'click' && event.target.closest('[type="submit"]') && !this.pill.value.trim()) {
        event.preventDefault(); // empty submit from the pill opens the panel instead
      }
      // Carry over anything already in the pill (e.g. browser autofill / back nav).
      if (this.pill && this.pill.value.trim() && !this.input.value) this.input.value = this.pill.value;
      this.header.openPanel('search', { trigger: null, focus: false });
    }

    _onOpen(event) {
      if (event.detail.panel !== 'search') return;
      this._closedByUser = false;
      this._lockScroll(window.matchMedia(MOBILE_QUERY).matches);
      this.input.focus({ preventScroll: true });
      const end = this.input.value.length;
      try {
        this.input.setSelectionRange(end, end);
      } catch (e) {
        /* type=search may not support selection in some browsers */
      }
      this._sync();
    }

    _onClose(event) {
      if (event.detail.panel !== 'search') return;
      const closedByUser = this._closedByUser;
      clearTimeout(this._timer);
      if (this._abort) this._abort.abort();
      this._setActive(-1);
      this.input.setAttribute('aria-expanded', 'false');
      if (closedByUser) {
        this.input.value = '';
        if (this.pill) this.pill.value = '';
        this._show('idle');
      } else if (this.pill) {
        this.pill.value = this.input.value;
      }
      this._lockScroll(false);
      if (closedByUser && this.pill) {
        this._restoring = true;
        this.pill.focus({ preventScroll: true });
        this._restoring = false;
      }
    }

    _lockScroll(on) {
      document.documentElement.classList.toggle('gs-hdr-scroll-locked', on);
    }

    _onBlockSelect(event) {
      if (this.contains(event.target) && this.header) this.header.openPanel('search', { focus: false });
    }

    _onBlockDeselect(event) {
      if (this.contains(event.target) && this.header) this.header.closePanel({ restoreFocus: false });
    }

    /* ---------- input / fetching ---------- */

    _onInput() {
      this._sync();
    }

    _query() {
      return this.input.value.trim();
    }

    _sync() {
      const q = this._query();
      clearTimeout(this._timer);
      if (!q) {
        if (this._abort) this._abort.abort();
        this._show('idle');
        return;
      }
      if (this._cache.has(q)) {
        this._render(q, this._cache.get(q));
        return;
      }
      this._show('loading');
      this._timer = setTimeout(() => this._fetch(q), DEBOUNCE_MS);
    }

    async _fetch(q) {
      if (this._abort) this._abort.abort();
      const controller = new AbortController();
      this._abort = controller;
      const base = this.dataset.suggestUrl || (window.routes && window.routes.predictive_search_url) || '/search/suggest';
      const params = new URLSearchParams({
        q,
        section_id: this.dataset.sectionId,
        'resources[type]': 'query,product',
        'resources[limit]': this.dataset.productsLimit || '6',
        'resources[limit_scope]': 'each',
      });
      try {
        const response = await fetch(`${base}?${params}`, { signal: controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const html = await response.text();
        if (controller.signal.aborted) return;
        if (this._cache.size > 40) this._cache.clear();
        this._cache.set(q, html);
        if (q === this._query()) this._render(q, html);
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        if (q !== this._query()) return;
        this.results.innerHTML = '';
        this._show('error');
      }
    }

    _render(q, html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const source = doc.querySelector('[data-gs-search-empty], .gs-hdr-search__cols');
      this._setActive(-1);
      if (!source) {
        this.results.innerHTML = '';
        this._show('empty');
        return;
      }
      this.results.innerHTML = source.outerHTML;
      // Lazy images inside a freshly injected node still load; Judge.me badges are static HTML.
      this._show(source.hasAttribute('data-gs-search-empty') ? 'empty' : 'results');
      const count = this.results.querySelectorAll('[role="option"]').length;
      this._announce(count ? this._t('results') : this._t('empty'));
    }

    _t(key) {
      const map = this.status ? this.status.dataset : {};
      return map[key] || '';
    }

    _announce(text) {
      if (this.status) this.status.textContent = text;
    }

    _show(state) {
      this.body.dataset.gsSearchState = state;
      const idle = state === 'idle';
      this.idle.hidden = !idle;
      this.live.hidden = idle;
      this.loading.hidden = state !== 'loading';
      this.results.hidden = state === 'loading' && !this.results.innerHTML.trim();
      this.body.classList.toggle('is-loading', state === 'loading');
      const expanded = state === 'results';
      this.input.setAttribute('aria-expanded', String(expanded));
      if (state === 'idle') this._announce('');
    }

    /* ---------- keyboard ---------- */

    _options() {
      return Array.from(this.results.querySelectorAll('[role="option"]'));
    }

    _setActive(index) {
      const options = this._options();
      options.forEach((o) => o.setAttribute('aria-selected', 'false'));
      this._active = index;
      if (index < 0 || !options[index]) {
        this._active = -1;
        this.input.removeAttribute('aria-activedescendant');
        return;
      }
      const option = options[index];
      option.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', option.id);
      option.scrollIntoView({ block: 'nearest' });
    }

    _onKeydown(event) {
      if (event.key === 'Escape') {
        this._closedByUser = true;
        return; // <gs-header> closes the panel
      }
      if (event.target !== this.input) return;
      const options = this._options();
      const usable = this.body.dataset.gsSearchState === 'results' && options.length;
      switch (event.key) {
        case 'ArrowDown':
          if (!usable) return;
          event.preventDefault();
          this._setActive((this._active + 1) % options.length);
          break;
        case 'ArrowUp':
          if (!usable) return;
          event.preventDefault();
          this._setActive(this._active <= 0 ? options.length - 1 : this._active - 1);
          break;
        case 'Enter': {
          if (usable && this._active > -1) {
            const link = options[this._active].querySelector('a[href]');
            if (link) {
              event.preventDefault();
              window.location.href = link.href;
            }
          }
          break;
        }
        default:
      }
    }

    _onSubmit(event) {
      if (!this._query()) event.preventDefault();
    }

    _onResultsClick(event) {
      // Clicking a suggestion navigates via its own link; nothing to intercept.
      const option = event.target.closest('[role="option"]');
      if (option) this._setActive(this._options().indexOf(option));
    }
  }

  customElements.define('gs-header-search', GSHeaderSearch);
}
